import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./css/LandingPage-Blogs.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faExclamationTriangle,
  faUserShield,
  faLightbulb,
  faArrowRight,
  faCalendarAlt,
  faUser,
  faShield,
  faHeart,
  faDollarSign,
  faLaptop,
  faUsers,
  faMedkit,
  faPhone,
  faEnvelope,
  faGraduationCap,
  faComments,
  faLock,
  faShoppingCart,
} from "@fortawesome/free-solid-svg-icons";
import blogsAPI from "../app/services/BlogsAPI";
import { formatDateOnly, formatDateForBlog } from "../app/utils/DateTimeUtil";

function LandingPage_Blogs() {
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoadingBlogs, setIsLoadingBlogs] = useState(true);

  // Map category -> icon (shared semantics with MoreBlogs component)
  const categoryIconMap = {
    Security: faShield,
    "Romance Scams": faHeart,
    "Financial Protection": faDollarSign,
    "Tech Security": faLaptop,
    "Family Safety": faUsers,
    "Healthcare Fraud": faMedkit,
    "Phone Security": faPhone,
    "Email Security": faEnvelope,
    "Digital Literacy": faGraduationCap,
    "Social Media Scams": faComments,
    Privacy: faLock,
    "Digital Security": faShieldAlt,
    "Online Shopping": faShoppingCart,
  };

  // Fallback if icon missing
  const getCategoryIcon = (category) =>
    categoryIconMap[category] || faExclamationTriangle;

  // Function to load recent blog data from API
  const loadBlogData = async () => {
    console.log("🔄 Loading recent blog data from API...");
    setIsLoadingBlogs(true);

    try {
      // Fetch 6 recent blogs from the API
      const blogs = await blogsAPI.getRecentBlogs(6);

      const blogData = blogs.map((blog) => ({
        id: blog.id, // Use actual blog ID from database
        title: blog.title,
        summary: blog.summary,
        date: blog.published_date, // API uses published_date
        author: blog.author,
        category: blog.category,
        readTime:
          blog.readTime && !blog.readTime.includes("min")
            ? `${blog.readTime} min read`
            : blog.readTime || "5 min read",
        icon: getCategoryIcon(blog.category),
        link: `/blogs/${blog.short_title || blog.shortTitle}`, // Route with short title to our blog component
      }));

      // Add fallback blog if we have fewer than 6 blogs
      if (blogData.length < 6) {
        const fallbackBlogs = [
          {
            id: null, // No ID for fallback blogs
            title: "Tech Support Scams: What Every Senior Needs to Know",
            summary:
              "Understand how fake tech support calls work and learn foolproof methods to identify and avoid these increasingly sophisticated computer scams.",
            date: "August 5, 2025",
            author: "Jennifer Adams",
            category: "Tech Security",
            icon: getCategoryIcon("Tech Security"),
            readTime: "6 min read",
            link: null, // No link for fallback blogs
          },
          {
            id: null,
            title: "Phishing Emails: The Complete Protection Guide",
            summary:
              "Learn to identify sophisticated phishing attempts and protect your personal information from cybercriminals targeting your inbox.",
            date: "August 3, 2025",
            author: "Michael Chen",
            category: "Email Security",
            icon: getCategoryIcon("Email Security"),
            readTime: "8 min read",
            link: null,
          },
          {
            id: null,
            title:
              "Social Media Privacy: Essential Settings Everyone Should Know",
            summary:
              "Discover the privacy settings that matter most and how to configure your social media accounts for maximum security and peace of mind.",
            date: "August 1, 2025",
            author: "Sarah Johnson",
            category: "Privacy",
            icon: getCategoryIcon("Privacy"),
            readTime: "5 min read",
            link: null,
          },
          {
            id: null,
            title: "Online Shopping Safety: Avoid Scams and Protect Your Money",
            summary:
              "Essential tips for safe online shopping, including how to spot fake websites, secure payment methods, and protect your financial information.",
            date: "July 30, 2025",
            author: "David Rodriguez",
            category: "Online Shopping",
            icon: getCategoryIcon("Online Shopping"),
            readTime: "7 min read",
            link: null,
          },
          {
            id: null,
            title: "Phone Scams: Complete Protection Guide for Seniors",
            summary:
              "Master the art of identifying and stopping phone scams, from robocalls to sophisticated social engineering attacks targeting older adults.",
            date: "July 28, 2025",
            author: "Lisa Thompson",
            category: "Phone Security",
            icon: getCategoryIcon("Phone Security"),
            readTime: "6 min read",
            link: null,
          },
          {
            id: null,
            title: "Password Security: Creating Unbreakable Digital Defenses",
            summary:
              "Learn to create strong, unique passwords and use password managers effectively to secure all your online accounts.",
            date: "July 25, 2025",
            author: "Mark Wilson",
            category: "Digital Security",
            icon: getCategoryIcon("Digital Security"),
            readTime: "5 min read",
            link: null,
          },
        ];

        // Add fallback blogs to reach 6 total
        const neededFallbacks = 6 - blogData.length;
        blogData.push(...fallbackBlogs.slice(0, neededFallbacks));
      }

      setBlogPosts(blogData);
      console.log(
        `✅ Successfully loaded ${blogData.length} blog posts from API`,
      );
    } catch (error) {
      console.error("❌ Error loading blog data from API:", error);

      // Fallback to static blog data if API fails
      const fallbackBlogData = [
        {
          id: null,
          title: "Tech Support Scams: What Every Senior Needs to Know",
          summary:
            "Understand how fake tech support calls work and learn foolproof methods to identify and avoid these increasingly sophisticated computer scams.",
          date: "August 5, 2025",
          author: "Jennifer Adams",
          category: "Tech Security",
          icon: getCategoryIcon("Tech Security"),
          readTime: "6 min read",
          link: null,
        },
        {
          id: null,
          title: "Phishing Emails: The Complete Protection Guide",
          summary:
            "Learn to identify sophisticated phishing attempts and protect your personal information from cybercriminals targeting your inbox.",
          date: "August 3, 2025",
          author: "Michael Chen",
          category: "Email Security",
          icon: getCategoryIcon("Email Security"),
          readTime: "8 min read",
          link: null,
        },
        {
          id: null,
          title:
            "Social Media Privacy: Essential Settings Everyone Should Know",
          summary:
            "Discover the privacy settings that matter most and how to configure your social media accounts for maximum security and peace of mind.",
          date: "August 1, 2025",
          author: "Sarah Johnson",
          category: "Privacy",
          icon: getCategoryIcon("Privacy"),
          readTime: "5 min read",
          link: null,
        },
        {
          id: null,
          title: "Online Shopping Safety: Avoid Scams and Protect Your Money",
          summary:
            "Essential tips for safe online shopping, including how to spot fake websites, secure payment methods, and protect your financial information.",
          date: "July 30, 2025",
          author: "David Rodriguez",
          category: "Online Shopping",
          icon: getCategoryIcon("Online Shopping"),
          readTime: "7 min read",
          link: null,
        },
        {
          id: null,
          title: "Phone Scams: Complete Protection Guide for Seniors",
          summary:
            "Master the art of identifying and stopping phone scams, from robocalls to sophisticated social engineering attacks targeting older adults.",
          date: "July 28, 2025",
          author: "Lisa Thompson",
          category: "Phone Security",
          icon: getCategoryIcon("Phone Security"),
          readTime: "6 min read",
          link: null,
        },
        {
          id: null,
          title: "Password Security: Creating Unbreakable Digital Defenses",
          summary:
            "Learn to create strong, unique passwords and use password managers effectively to secure all your online accounts.",
          date: "July 25, 2025",
          author: "Mark Wilson",
          category: "Digital Security",
          icon: getCategoryIcon("Digital Security"),
          readTime: "5 min read",
          link: null,
        },
      ];

      setBlogPosts(fallbackBlogData);
      console.log("⚠️ Using fallback blog data due to API error");
    } finally {
      setIsLoadingBlogs(false);
    }
  };

  // Load blog data when component mounts
  useEffect(() => {
    loadBlogData();
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    document.documentElement.scrollTop = 0;
  };

  return (
    <div className="landing-page-blogs">
      <div className="scroll-to-top-link" onClick={scrollToTop}>
        Top ↑
      </div>
      {/* Header Section */}
      <div className="blogs-header">
        {/* <h2>Latest Insights & Protection Tips</h2> */}
        <h2>Blogs and Newsletters</h2>
        <p className="blogs-subtitle">
          Stay informed with expert advice on protecting yourself and your loved
          ones from online scams and fraud.
        </p>
      </div>

      {/* Blog Cards Grid */}
      <div className="blogs-grid">
        {isLoadingBlogs ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gridColumn: "1 / -1",
              padding: "40px",
              color: "#666",
            }}
          >
            <h3>Loading blogs...</h3>
          </div>
        ) : blogPosts.length === 0 ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gridColumn: "1 / -1",
              padding: "40px",
              color: "#666",
            }}
          >
            <h3>No blogs available at the moment.</h3>
          </div>
        ) : (
          blogPosts.map((post) =>
            post.id ? (
              <Link
                key={post.id}
                to={post.link}
                className="blog-card blog-card-clickable"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <div className="blog-card-header">
                  <div className="blog-icon">
                    <FontAwesomeIcon icon={post.icon} />
                  </div>
                  <div className="blog-meta">
                    <span className="blog-category">{post.category}</span>
                    <span className="blog-read-time">{post.readTime}</span>
                  </div>
                </div>

                <div className="blog-content">
                  <h3 className="blog-title">{post.title}</h3>
                  <p className="blog-summary">{post.summary}</p>
                </div>

                <div className="blog-footer">
                  <div className="blog-author-info">
                    <FontAwesomeIcon icon={faUser} className="author-icon" />
                    <span className="blog-author">{post.author}</span>
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      className="date-icon"
                    />
                    <span className="blog-date">
                      {formatDateForBlog(post.date)}
                    </span>
                  </div>
                  <span className="read-more-inline">
                    Read More <FontAwesomeIcon icon={faArrowRight} />
                  </span>
                </div>
              </Link>
            ) : (
              <div key={post.title + Math.random()} className="blog-card">
                <div className="blog-card-header">
                  <div className="blog-icon">
                    <FontAwesomeIcon icon={post.icon} />
                  </div>
                  <div className="blog-meta">
                    <span className="blog-category">{post.category}</span>
                    <span className="blog-read-time">{post.readTime}</span>
                  </div>
                </div>

                <div className="blog-content">
                  <h3 className="blog-title">{post.title}</h3>
                  <p className="blog-summary">{post.summary}</p>
                </div>

                <div className="blog-footer">
                  <div className="blog-author-info">
                    <FontAwesomeIcon icon={faUser} className="author-icon" />
                    <span className="blog-author">{post.author}</span>
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      className="date-icon"
                    />
                    <span className="blog-date">
                      {formatDateForBlog(post.published_date || post.date)}
                    </span>
                  </div>
                  <button
                    className="read-more-link"
                    disabled
                    style={{ opacity: 0.6, cursor: "not-allowed" }}
                  >
                    Coming Soon
                  </button>
                </div>
              </div>
            ),
          )
        )}
      </div>

      {/* More Blogs Button */}
      <div className="more-blogs-section">
        <Link to="/blogs" className="more-blogs-btn">
          <span>More Blogs</span>
          <FontAwesomeIcon icon={faArrowRight} />
        </Link>
      </div>
    </div>
  );
}

export default LandingPage_Blogs;
